import { useCallback, useMemo, useRef } from 'react';
import {
  useSorApi,
  type SorQueryPayload,
  type SorQueryResponse,
  type SorRecord,
} from '@circuitry-ai/doc-ui';
import type { SorDefinitionSummary } from '../work-queues/work-queues-types';
import {
  buildItemRecordData,
  buildSectionRecordData,
  buildTemplateRecordData,
  groupItemsByTemplate,
  hasItemChanged,
  mapInspectionTemplate,
  normalizeCategory,
  sortItems,
} from './inspection-templates-mappers';
import {
  INSPECTION_TEMPLATE_DEFINITION,
  INSPECTION_TEMPLATE_ITEM_DEFINITION,
  type InspectionTemplate,
  type InspectionTemplateItemDraft,
  type InspectionTemplateItemRecord,
  type InspectionTemplateItemRecordData,
  type InspectionTemplateRecord,
  type InspectionTemplateRecordData,
} from './inspection-templates-types';

const PAGE_SIZE = 25;
// Safety stop so a bad `total` can't loop forever; items of every template are read in one query
const MAX_PAGES = 200;
const DEFINITIONS_PAGE_SIZE = 200;
const DEFINITIONS_BUSINESS_DOMAIN = 'slm';

type SorPage<T> = { items: T[]; total: number };
type BulkCreateResult = { created: number; failed: number; errors: unknown[] };

interface InspectionDefinitions {
  template: string;
  item: string;
}

/** The definition's last key segment, e.g. `inspection_template` */
const definitionKeySuffix = (definition: string) => definition.split('.').pop() ?? definition;

/** Picks the published definition for `fallback` (matched by its key), so a module segment such as `slm.insp.*` doesn't matter */
function findDefinitionKey(definitions: SorDefinitionSummary[], fallback: string): string {
  const key = definitionKeySuffix(fallback);
  const match = definitions.find(
    (definition) => definition.qualified_key === fallback
      || definition.key === key
      || definition.qualified_key?.endsWith(`.${key}`),
  );
  return match?.qualified_key || fallback;
}

/**
 * Inspection templates on the SOR records API:
 * - templates: slm.inspection_template records (the grid rows)
 * - items:     slm.inspection_template_item records; data.templateId = template record id
 */
export function useInspectionTemplatesApi() {
  const { sorFetch, queryRecords, connectSor } = useSorApi();
  const definitionsRef = useRef<Promise<InspectionDefinitions> | null>(null);
  const sharedSectionRef = useRef<Promise<string> | null>(null);

  const getDefinitions = useCallback((): Promise<InspectionDefinitions> => {
    if (!definitionsRef.current) {
      const params = new URLSearchParams({
        business_domain: DEFINITIONS_BUSINESS_DOMAIN,
        status: 'PUBLISHED',
        include_permissions: 'false',
        limit: String(DEFINITIONS_PAGE_SIZE),
      });
      definitionsRef.current = sorFetch<SorPage<SorDefinitionSummary>>(`/api/v1/definitions?${params}`)
        .then((response) => {
          const definitions = response?.items ?? [];
          return {
            template: findDefinitionKey(definitions, INSPECTION_TEMPLATE_DEFINITION),
            item: findDefinitionKey(definitions, INSPECTION_TEMPLATE_ITEM_DEFINITION),
          };
        })
        .catch((error: unknown) => {
          // Not cached, so a later call retries (e.g. after connecting to SOR)
          definitionsRef.current = null;
          throw error;
        });
    }
    return definitionsRef.current;
  }, [sorFetch]);

  const queryAll = useCallback(
    async <TData>(payload: Omit<SorQueryPayload, 'page'>, signal?: AbortSignal) => {
      const items: SorRecord<TData>[] = [];
      for (let pageIndex = 0; pageIndex < MAX_PAGES; pageIndex++) {
        const response = await queryRecords<SorQueryResponse<TData>>(
          {
            include_deleted: false,
            ...payload,
            page: { limit: PAGE_SIZE, offset: items.length },
          },
          signal,
        );
        const pageItems = response?.items ?? [];
        items.push(...pageItems);
        if (!pageItems.length || items.length >= (response?.total ?? 0)) break;
      }
      return items;
    },
    [queryRecords],
  );

  /** Checklist items (sections excluded) of the given templates, in one query without paging */
  const fetchItemsForTemplates = useCallback(
    async (itemDefinition: string, templateIds: string[], signal?: AbortSignal) => {
      if (!templateIds.length) return [];
      const response = await queryRecords<SorQueryResponse<InspectionTemplateItemRecordData>>(
        {
          definition: itemDefinition,
          filter: {
            and: [
              { field: 'data.templateId', op: 'in', value: templateIds },
              { field: 'data.itemType', op: 'nin', value: ['SECTION'] },
            ],
          },
          sort: [],
          search: null,
          state: null,
          include_deleted: false,
        },
        signal,
      );
      return response?.items ?? [];
    },
    [queryRecords],
  );

  const fetchTemplates = useCallback(
    async (signal?: AbortSignal): Promise<InspectionTemplate[]> => {
      const definitions = await getDefinitions();
      const records = await queryAll<InspectionTemplateRecordData>(
        { definition: definitions.template, filter: null, search: null, state: null },
        signal,
      );
      const itemsByTemplate = await fetchItemsForTemplates(
        definitions.item,
        records.map((record) => record.id),
        signal,
      )
        .then(groupItemsByTemplate)
        // The list still loads, without counts, if the items can't be read
        .catch((error: unknown) => {
          if (!signal?.aborted) console.error('Failed to load inspection template items', error);
          return null;
        });
      return records
        .map((record) => mapInspectionTemplate(
          record,
          itemsByTemplate ? itemsByTemplate.get(record.id) ?? [] : null,
        ))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    [fetchItemsForTemplates, getDefinitions, queryAll],
  );

  const fetchTemplateItems = useCallback(
    async (templateId: string, signal?: AbortSignal): Promise<InspectionTemplateItemRecord[]> => {
      const definitions = await getDefinitions();
      const items = await queryAll<InspectionTemplateItemRecordData>(
        {
          definition: definitions.item,
          filter: { field: 'data.templateId', op: 'eq', value: templateId },
        },
        signal,
      );
      // Guard: saving deletes items missing from the editor, so never act on another template's records
      return sortItems(items.filter((item) => item.data?.templateId === templateId));
    },
    [getDefinitions, queryAll],
  );

  /** templateKeys already used, including deleted templates, so a new key never collides */
  const fetchTemplateKeys = useCallback(async (): Promise<string[]> => {
    const definitions = await getDefinitions();
    const records = await queryAll<InspectionTemplateRecordData>({
      definition: definitions.template,
      filter: null,
      include_deleted: true,
    });
    return records
      .map((record) => record.data?.templateKey ?? record.natural_key ?? '')
      .filter(Boolean);
  }, [getDefinitions, queryAll]);

  const createRecord = useCallback(
    <TData>(definition: string, data: TData) => sorFetch<SorRecord<TData>>(
      `/api/v1/records?definition=${encodeURIComponent(definition)}`,
      { method: 'POST', body: JSON.stringify({ data }) },
    ),
    [sorFetch],
  );

  const bulkCreateItems = useCallback(
    async (records: InspectionTemplateItemRecordData[]) => {
      if (!records.length) return;
      const definitions = await getDefinitions();
      const result = await sorFetch<BulkCreateResult>(
        `/api/v1/records/bulk?definition=${encodeURIComponent(definitions.item)}`,
        {
          method: 'POST',
          body: JSON.stringify({ atomic: true, records: records.map((data) => ({ data })) }),
        },
      );
      if (result?.failed) {
        throw new Error(
          `${result.failed} template item(s) could not be created: ${JSON.stringify(result.errors)}`,
        );
      }
    },
    [getDefinitions, sorFetch],
  );

  const deleteRecord = useCallback(
    (record: Pick<SorRecord, 'id' | 'revision'>, hard?: boolean) => {
      const params = new URLSearchParams({ expected_revision: String(record.revision) });
      if (hard !== undefined) params.set('hard', String(hard));
      return sorFetch<void>(`/api/v1/records/${record.id}?${params}`, { method: 'DELETE' });
    },
    [sorFetch],
  );

  /** Deletes items children first, since SOR refuses to delete a section other items still reference */
  const deleteItems = useCallback(
    async (items: InspectionTemplateItemRecord[]) => {
      let remaining = items;
      while (remaining.length) {
        const parentIds = new Set(remaining.map((item) => item.data?.parentItemId).filter(Boolean));
        const leaves = remaining.filter((item) => !parentIds.has(item.id));
        // A parent cycle can't be ordered; delete the rest together and let SOR report it
        const batch = leaves.length ? leaves : remaining;
        await Promise.all(batch.map((item) => deleteRecord(item)));
        remaining = remaining.filter((item) => !batch.includes(item));
      }
    },
    [deleteRecord],
  );

  /** Id of the one section every template's questions go under: the oldest section record, if any */
  const findSharedSectionId = useCallback(async (): Promise<string | null> => {
    const definitions = await getDefinitions();
    const sections = await queryAll<InspectionTemplateItemRecordData>({
      definition: definitions.item,
      filter: { field: 'data.itemType', op: 'eq', value: 'SECTION' },
    });
    const [oldest] = [...sections].sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''));
    return oldest?.id ?? null;
  }, [getDefinitions, queryAll]);

  /**
   * The shared section, created (in `templateId`, since a section needs one) when there is none.
   * Concurrent calls share one lookup so two saves can't each create a section.
   */
  const getSharedSectionId = useCallback(
    (templateId: string, takenItemKeys: Set<string>): Promise<string> => {
      sharedSectionRef.current ??= (async () => {
        const existing = await findSharedSectionId();
        if (existing) return existing;
        const definitions = await getDefinitions();
        const section = await createRecord(definitions.item, buildSectionRecordData(templateId, takenItemKeys));
        return section.id;
      })().finally(() => {
        sharedSectionRef.current = null;
      });
      return sharedSectionRef.current;
    },
    [createRecord, findSharedSectionId, getDefinitions],
  );

  /** Copies the questions into another template, under the shared section; sections aren't copied */
  const copyItems = useCallback(
    async (source: InspectionTemplateItemRecord[], templateId: string) => {
      const questions = sortItems(source.filter((item) => item.data?.itemType !== 'SECTION'));
      if (!questions.length) return;
      const takenItemKeys = new Set(
        source.map((item) => item.data?.itemKey).filter((key): key is string => !!key),
      );
      const parentItemId = await getSharedSectionId(templateId, takenItemKeys);
      await bulkCreateItems(questions.map((item) => ({ ...item.data, templateId, parentItemId })));
    },
    [bulkCreateItems, getSharedSectionId],
  );

  const createTemplate = useCallback(
    async (name: string): Promise<InspectionTemplate> => {
      const [definitions, keys] = await Promise.all([getDefinitions(), fetchTemplateKeys()]);
      const record = await createRecord(definitions.template, buildTemplateRecordData(name, keys));
      return mapInspectionTemplate(record as InspectionTemplateRecord, []);
    },
    [createRecord, fetchTemplateKeys, getDefinitions],
  );

  /** Saves the grid's inline name / type edits; the templateKey stays as it was */
  const updateTemplate = useCallback(
    async (template: InspectionTemplate, changes: { name: string; type: string }) => {
      const data: InspectionTemplateRecordData = {
        ...template.record.data,
        name: changes.name.trim(),
        category: normalizeCategory(changes.type),
      };
      await sorFetch(`/api/v1/records/${template.id}`, {
        method: 'PUT',
        body: JSON.stringify({ data, expected_revision: template.revision }),
      });
    },
    [sorFetch],
  );

  /**
   * Makes the template's checklist match the editor: deletes removed items, updates changed
   * ones and creates new ones. Every question is placed under the one shared section; the
   * template's other sections are deleted once nothing points at them.
   */
  const saveTemplateItems = useCallback(
    async (
      templateId: string,
      drafts: InspectionTemplateItemDraft[],
      originalItems: InspectionTemplateItemRecord[],
    ) => {
      const keptIds = new Set(drafts.map((draft) => draft.record?.id).filter(Boolean));
      const takenItemKeys = new Set(
        originalItems.map((item) => item.data?.itemKey).filter((key): key is string => !!key),
      );
      const removed = originalItems.filter(
        (item) => item.data?.itemType !== 'SECTION' && !keptIds.has(item.id),
      );

      await deleteItems(removed);
      if (!drafts.length) return;

      const sectionId = await getSharedSectionId(templateId, takenItemKeys);

      const changed = drafts
        .map((draft, position) => ({ draft, position }))
        .filter(({ draft, position }) => draft.record && hasItemChanged(draft, position, sectionId));
      await Promise.all(changed.map(({ draft, position }) => sorFetch(
        `/api/v1/records/${draft.record?.id}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            data: buildItemRecordData(draft, templateId, position, takenItemKeys, sectionId),
            expected_revision: draft.record?.revision,
          }),
        },
      )));

      await bulkCreateItems(
        drafts
          .map((draft, position) => ({ draft, position }))
          .filter(({ draft }) => !draft.record)
          .map(({ draft, position }) =>
            buildItemRecordData(draft, templateId, position, takenItemKeys, sectionId)),
      );

      // This template's questions have all moved to the shared section, so its own sections are
      // now unused. Best effort: SOR refuses to delete one that something still points at
      const extraSections = originalItems.filter(
        (item) => item.data?.itemType === 'SECTION' && item.id !== sectionId,
      );
      const results = await Promise.allSettled(extraSections.map((section) => deleteRecord(section)));
      results.forEach((result) => {
        if (result.status === 'rejected') console.warn('Could not delete unused inspection section', result.reason);
      });
    },
    [bulkCreateItems, deleteItems, deleteRecord, getSharedSectionId, sorFetch],
  );

  /**
   * Deletes the items first (they reference the template), then soft-deletes the template.
   * The shared section is kept, since other templates' questions are under it.
   */
  const deleteTemplate = useCallback(
    async (template: Pick<InspectionTemplate, 'id' | 'revision'>) => {
      const [items, sharedSectionId] = await Promise.all([
        fetchTemplateItems(template.id),
        findSharedSectionId(),
      ]);
      await deleteItems(items.filter((item) => item.id !== sharedSectionId));
      await deleteRecord(template, false);
    },
    [deleteItems, deleteRecord, fetchTemplateItems, findSharedSectionId],
  );

  const duplicateTemplate = useCallback(
    async (template: InspectionTemplate): Promise<InspectionTemplate> => {
      const [definitions, keys, items] = await Promise.all([
        getDefinitions(),
        fetchTemplateKeys(),
        fetchTemplateItems(template.id),
      ]);
      // The timestamp keeps repeated copies of the same template distinguishable
      const name = `${template.name} (Copy) ${Date.now()}`;
      const record = await createRecord(
        definitions.template,
        buildTemplateRecordData(name, keys, template.record.data),
      );
      try {
        await copyItems(items, record.id);
      } catch (error) {
        try {
          await deleteTemplate(record);
        } catch {
          // Preserve the original error; cleanup of the incomplete copy is best effort
        }
        throw error;
      }
      return mapInspectionTemplate(record as InspectionTemplateRecord, items);
    },
    [copyItems, createRecord, deleteTemplate, fetchTemplateItems, fetchTemplateKeys, getDefinitions],
  );

  return useMemo(
    () => ({
      fetchTemplates,
      fetchTemplateItems,
      createTemplate,
      updateTemplate,
      saveTemplateItems,
      duplicateTemplate,
      deleteTemplate,
      connectSor,
    }),
    [
      fetchTemplates,
      fetchTemplateItems,
      createTemplate,
      updateTemplate,
      saveTemplateItems,
      duplicateTemplate,
      deleteTemplate,
      connectSor,
    ],
  );
} 
